import React, { useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { dashboardEnvConfig } from '../../services/config'
import { humanizeScriptName } from '../bostonIA/lib/triage'
import { getDashboardLang, saveDashboardLang, makeT } from './i18n'
import BostonIAPanel from './BostonIAPanel'
import DashboardLoader from './DashboardLoader'
import DashboardScriptConfigModal from './DashboardScriptConfigModal'

const ADVANCED_MODE_KEY = 'boston_dashboard_advanced_mode'

function getDashboardAdvancedMode() {
  try {
    return window.localStorage.getItem(ADVANCED_MODE_KEY) === '1'
  } catch (_) {
    return false
  }
}

function saveDashboardAdvancedMode(enabled) {
  try {
    window.localStorage.setItem(ADVANCED_MODE_KEY, enabled ? '1' : '0')
  } catch (_) {
    /* no-op */
  }
}

function getQueryValue(name) {
  const query = window.location.search.length > 0 ? window.location.search.slice(1) : ''
  const parts = query.split('&')
  for (const part of parts) {
    const equalsIndex = part.indexOf('=')
    const key = equalsIndex >= 0 ? part.slice(0, equalsIndex) : part
    if (key !== name) continue
    const value = equalsIndex >= 0 ? part.slice(equalsIndex + 1) : ''
    try {
      return decodeURIComponent(value)
    } catch (_) {
      return value
    }
  }
  return ''
}

function getDashboardEnvironment() {
  const env = dashboardEnvConfig()
  return { key: env.apiKey, urlApi: env.baseURL }
}

function extractItems(data) {
  if (Array.isArray(data)) return data
  if (!data || typeof data !== 'object') return []
  for (const key of ['scripts', 'customScripts', 'data', 'items', 'results']) {
    if (Array.isArray(data[key])) return data[key]
    if (data[key] && typeof data[key] === 'object') {
      const nested = extractItems(data[key])
      if (nested.length > 0) return nested
    }
  }
  return []
}

function setQueryParams(href, params) {
  const [withoutHash, hash = ''] = String(href || '').split('#')
  const questionIndex = withoutHash.indexOf('?')
  const base = questionIndex >= 0 ? withoutHash.slice(0, questionIndex) : withoutHash
  const currentQuery = questionIndex >= 0 ? withoutHash.slice(questionIndex + 1) : ''
  const query = new URLSearchParams(currentQuery)

  Object.entries(params || {}).forEach(([key, value]) => {
    if (value !== undefined && value !== null && String(value).trim() !== '') {
      query.set(key, String(value))
    }
  })

  const search = query.toString()
  return `${base}${search ? `?${search}` : ''}${hash ? `#${hash}` : ''}`
}

function firstValue(...values) {
  return values.find((value) => value !== undefined && value !== null && String(value).trim() !== '') || ''
}

function toBool(value) {
  return value === true || value === 1 || value === '1' || value === 'true'
}

function formatDateShort(value, lang) {
  if (!value) return ''
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return ''
  return date.toLocaleDateString(lang === 'en' ? 'en-US' : 'es-AR', { day: '2-digit', month: '2-digit', year: 'numeric' })
}

function reviewUnreadMessageCount(item) {
  if (reviewIsReady(item)) return 0
  const direct = Number(item?.unreadMessages || 0)
  if (direct > 0) return direct
  return (Array.isArray(item?.messages) ? item.messages : [])
    .filter((message) => String(message.senderType || '').toLowerCase() === 'dev' && message.readByClient !== true)
    .length
}

function reviewIsReady(item) {
  const estado = String(item?.estado || item?.status || '').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '')
  return estado === 'aprobado' || estado === 'publicado'
}

function reviewStatusLabel(item, lang) {
  const estado = String(item?.estado || item?.status || '').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '')
  const labels = lang === 'en'
    ? { en_revision: 'In review', aprobado: 'Ready', publicado: 'Published', rechazado: 'Needs changes' }
    : { en_revision: 'En revision', aprobado: 'Listo', publicado: 'Publicado', rechazado: 'Requiere ajustes' }
  return labels[estado] || item?.statusLabel || item?.estado || (lang === 'en' ? 'Status unavailable' : 'Estado no disponible')
}

function reviewHistoryKey(value) {
  return String(value || '').trim().toLowerCase()
}

const SCRIPT_REQUEST_GUIDE = {
  good: [
    {
      title: 'Define objetivo y momento de uso',
      text: 'Crear un script que consulte las reglas de tarifa del PNR activo cuando el usuario presione "Consultar reglas".',
    },
    {
      title: 'Indica comandos y orden',
      text: 'Ejecutar WP y despues WPRD*C16. Mostrar la respuesta encontrada sin guardar remarks ni cerrar la reserva.',
    },
    {
      title: 'Describe campos esperados',
      text: 'Pedir centro de costo obligatorio, moneda obligatoria y codigo de autorizacion opcional.',
    },
    {
      title: 'Separa listas grandes',
      text: 'Si un campo tiene muchas opciones, ponlas en un bloque separado. Ejemplo: "El campo Centro de costo debe ser un select con estas opciones:" y debajo lista CC01, CC02, CC03... Esto permite crear un catalogo externo y cargarlo desde catalogs.json.',
    },
    {
      title: 'Aclara validaciones',
      text: 'Si no hay PNR activo, mostrar el error crudo de Sabre y no continuar.',
    },
    {
      title: 'Ejemplo de prompt',
      text: 'Necesito un script para documentar información de un viaje corporativo. Debe pedir los datos Número de empleado, Motivo del viaje, Centro de costos y Proyecto. El número de empleado debe ser obligatorio. Quiero que el número de empleado y el centro de costos se guarden como remarks generales, el motivo del viaje como remark histórico y el proyecto como remark confidencial. El formulario puede llamarse DATOS DEL VIAJE.',
    },
  ],
  bad: [
    {
      title: 'Solicitud demasiado general',
      text: 'Hazme un script de tarifas.',
    },
    {
      title: 'Sin evento claro',
      text: 'Que consulte algo y luego muestre datos.',
    },
    {
      title: 'Sin reglas de guardado',
      text: 'Pon los remarks necesarios.',
    },
    {
      title: 'Listas mezcladas en el texto',
      text: 'Centro de costo CC01 CC02 CC03 y departamentos DEP01 DEP02 DEP03 todo dentro del mismo parrafo. Es mejor separar cada lista con su titulo para que la IA identifique cada catalogo.',
    },
    {
      title: 'Sin manejo de errores',
      text: 'Que funcione con cualquier PNR.',
    },
    {
      title: 'Ejemplo ambiguo',
      text: 'Necesito un script para documentar información de un viaje corporativo. Debe pedir los datos Número de empleado, Motivo del viaje, Centro de costos y Proyecto. El número de empleado debe ser obligatorio. Quiero que la información capturada se guarde como remarks en la reserva. El formulario puede llamarse DATOS DEL VIAJE.',
    },
  ],
}

function buildScriptUrl(url, token, script = {}, advancedMode = false) {
  let href = String(url || '').trim()
  if (href === '') return '#'

  if (href.includes('{token}')) {
    href = href.split('{token}').join('')
  }

  return setQueryParams(href, {
    token,
    type: firstValue(script.type, script.typeof, script.type_of),
    pcc: firstValue(script.pcc, script.PCC, script.pcc_code),
    user: firstValue(script.user, script.username, script.usuario),
    advancedMode: advancedMode ? 1 : 0,
  })
}

async function getJson(url, headers, timeoutMs) {
  const controller = new AbortController()
  const timer = window.setTimeout(() => controller.abort(), timeoutMs || 12000)
  let response
  try {
    response = await fetch(url, {
      method: 'GET',
      headers,
      cache: 'no-store',
      signal: controller.signal,
    })
  } catch (error) {
    if (error && error.name === 'AbortError') {
      throw new Error(`Timeout al consultar ${url}`)
    }
    throw error
  } finally {
    window.clearTimeout(timer)
  }

  const text = await response.text()
  let data = null
  try {
    data = text !== '' ? JSON.parse(text) : null
  } catch (_) {
    data = text
  }
  if (!response.ok) {
    let where = url
    try { where = new URL(url).pathname } catch (_) {}
    const apiMsg = data && typeof data === 'object' ? (data.message || data.error || '') : (typeof data === 'string' ? data.slice(0, 120) : '')
    throw new Error(`HTTP ${response.status} en ${where}${apiMsg ? ' — ' + apiMsg : ''}`)
  }
  return data
}

async function postJson(url, headers, body, timeoutMs) {
  const controller = new AbortController()
  const timer = window.setTimeout(() => controller.abort(), timeoutMs || 12000)
  let response
  try {
    response = await fetch(url, {
      method: 'POST',
      headers,
      cache: 'no-store',
      body: JSON.stringify(body || {}),
      signal: controller.signal,
    })
  } catch (error) {
    if (error && error.name === 'AbortError') {
      throw new Error(`Timeout al consultar ${url}`)
    }
    throw error
  } finally {
    window.clearTimeout(timer)
  }

  const text = await response.text()
  let data = null
  try {
    data = text !== '' ? JSON.parse(text) : null
  } catch (_) {
    data = text
  }
  if (!response.ok) {
    let where = url
    try { where = new URL(url).pathname } catch (_) {}
    const apiMsg = data && typeof data === 'object' ? (data.message || data.error || '') : (typeof data === 'string' ? data.slice(0, 120) : '')
    throw new Error(`HTTP ${response.status} en ${where}${apiMsg ? ' — ' + apiMsg : ''}`)
  }
  return data
}

function DashboardPage() {
  const dashboardFooterRef = useRef(null)
  // Idioma del dashboard (ES/EN) — solo esta vista, persistido en localStorage.
  const [lang, setLang] = useState(getDashboardLang())
  const [advancedMode, setAdvancedMode] = useState(getDashboardAdvancedMode())
  const t = useMemo(() => makeT(lang), [lang])
  function changeLang(next) {
    setLang(next)
    saveDashboardLang(next)
  }
  function changeAdvancedMode(next) {
    const enabled = next === true
    setAdvancedMode(enabled)
    saveDashboardAdvancedMode(enabled)
  }
  const [active, setActive] = useState(false)
  const [loading, setLoading] = useState(true)
  const [message, setMessage] = useState(makeT(getDashboardLang())('loadingScripts'))
  const [messageIcon, setMessageIcon] = useState('repeat')
  const [scripts, setScripts] = useState([])
  const [repoScripts, setRepoScripts] = useState([])
  const [repoLoading, setRepoLoading] = useState(false)
  const [repoError, setRepoError] = useState('')
  const [repoBusyUuid, setRepoBusyUuid] = useState('')
  const [resendBusyUuid, setResendBusyUuid] = useState('')
  const [repoMessage, setRepoMessage] = useState('')
  const [reviewHistory, setReviewHistory] = useState([])
  const [reviewHistoryLoading, setReviewHistoryLoading] = useState(false)
  const [reviewHistoryError, setReviewHistoryError] = useState('')
  const [reviewHistoryOpen, setReviewHistoryOpen] = useState(false)
  const [reviewClearBusy, setReviewClearBusy] = useState(false)
  const [reviewReplyDrafts, setReviewReplyDrafts] = useState({})
  const [reviewReplyBusyId, setReviewReplyBusyId] = useState('')
  // --- Modal de distribución por PCC / Usuario ---
  const [distScript, setDistScript] = useState(null)
  const [distPccs, setDistPccs] = useState([])
  const [distUsers, setDistUsers] = useState([])
  const [distSelectedPcc, setDistSelectedPcc] = useState('')
  const [distApplyOnlyPcc, setDistApplyOnlyPcc] = useState(false)
  const [distSelectedUsers, setDistSelectedUsers] = useState([])
  const [distLoading, setDistLoading] = useState(false)
  const [distUsersLoading, setDistUsersLoading] = useState(false)
  const [distBusy, setDistBusy] = useState(false)
  const [distMsg, setDistMsg] = useState('')
  const [repoPreview, setRepoPreview] = useState(null) // { name, url } para la vista previa del repo
  const [configScript, setConfigScript] = useState(null)
  // --- Eliminar script del repositorio (borra asignaciones + da de baja el script) ---
  const [deleteTarget, setDeleteTarget] = useState(null)
  const [resendTarget, setResendTarget] = useState(null) // window.confirm está BLOQUEADO en el iframe de Sabre: modal propio
  const [resendFeedback, setResendFeedback] = useState('') // motivo del reenvío (lo ve el dev en Revisión Dev)
  const [deleteBusy, setDeleteBusy] = useState(false)
  const [deleteMsg, setDeleteMsg] = useState('')
  // --- Constructor IA flotante ---
  const [iaOpen, setIaOpen] = useState(false)
  const [iaFloatBottom, setIaFloatBottom] = useState(12)
  const [scriptGuideOpen, setScriptGuideOpen] = useState(false)
  // --- Descripciones expandidas ("leer más") en grid y repositorio ---
  const [expandedDescs, setExpandedDescs] = useState([])
  function toggleDesc(key) {
    setExpandedDescs((list) => (list.includes(key) ? list.filter((k) => k !== key) : [...list, key]))
  }
  const [token, setToken] = useState('')
  const [search, setSearch] = useState('')
  const [page, setPage] = useState(1)
  const [repoSearch, setRepoSearch] = useState('')
  const [repoPage, setRepoPage] = useState(1)
  const [dashboardRuntimeSession, setDashboardRuntimeSession] = useState({
    username: '',
    parentPcc: '',
    pcc: '',
  })
  const [dashboardPermissions, setDashboardPermissions] = useState({
    enableScriptGenerator: false,
    enableRepositories: false,
  })
  const [reloadTick, setReloadTick] = useState(0)
  const canUseScriptGenerator = dashboardPermissions.enableScriptGenerator === true
  const canUseRepositories = dashboardPermissions.enableRepositories === true
  const perPage = 12
  const repoPerPage = 5

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase()
    if (term === '') return scripts
    return scripts.filter((script) => String(script.name || script.namescript || '')
      .toLowerCase()
      .includes(term))
  }, [scripts, search])

  const totalPages = Math.max(1, Math.ceil(filtered.length / perPage))
  const currentPage = Math.min(page, totalPages)
  const pageItems = filtered.slice((currentPage - 1) * perPage, currentPage * perPage)
  const filteredRepoScripts = useMemo(() => {
    const term = repoSearch.trim().toLowerCase()
    if (term === '') return repoScripts
    return repoScripts.filter((script) => [
      script.name,
      script.namescript,
      script.title,
      humanizeScriptName(String(script.name || script.namescript || script.title || '')),
      script.uuid,
      script.url,
      script.description,
      script.feedback,
      script.status,
      script.statusLabel,
    ].join(' ').toLowerCase().includes(term))
  }, [repoScripts, repoSearch])
  const repoTotalPages = Math.max(1, Math.ceil(filteredRepoScripts.length / repoPerPage))
  const currentRepoPage = Math.min(repoPage, repoTotalPages)
  const repoPageItems = filteredRepoScripts.slice((currentRepoPage - 1) * repoPerPage, currentRepoPage * repoPerPage)
  const reviewReadyCount = useMemo(() => {
    return reviewHistory.filter((item) => reviewIsReady(item)).length
  }, [reviewHistory])
  const reviewUnreadCount = useMemo(() => {
    return reviewHistory.reduce((total, item) => total + reviewUnreadMessageCount(item), 0)
  }, [reviewHistory])
  const reviewNotificationCount = reviewReadyCount + reviewUnreadCount
  const visibleReviewHistory = reviewHistory
  const reviewedReadyScripts = useMemo(() => {
    const keys = new Set()
    reviewHistory.forEach((item) => {
      if (!reviewIsReady(item)) return
      const scriptUUID = reviewHistoryKey(item.scriptUuid || item.uuid)
      const title = reviewHistoryKey(item.title || item.name)
      const titleHuman = reviewHistoryKey(humanizeScriptName(String(item.title || item.name || '')))
      if (scriptUUID) keys.add(`uuid:${scriptUUID}`)
      if (title) keys.add(`title:${title}`)
      if (titleHuman) keys.add(`title:${titleHuman}`)
    })
    return keys
  }, [reviewHistory])
  const usersForSelectedPcc = useMemo(() => {
    if (!distSelectedPcc) return []
    return distUsers.filter((user) => String(user.pcc || '').toUpperCase() === distSelectedPcc)
  }, [distUsers, distSelectedPcc])
  const selectedUsersForPcc = useMemo(() => {
    return usersForSelectedPcc.filter((user) => distSelectedUsers.includes(`${user.username}|${user.pcc}`))
  }, [usersForSelectedPcc, distSelectedUsers])
  const distributionTargetCount = distApplyOnlyPcc
    ? (distSelectedPcc ? 1 : 0)
    : selectedUsersForPcc.length
  const canDistribute = !distLoading && !distUsersLoading && !distBusy && (
    distApplyOnlyPcc
      ? Boolean(distSelectedPcc)
      : Boolean(distSelectedPcc) && selectedUsersForPcc.length > 0
  )

  async function loadDashboardReviewHistory(headers, env) {
    setReviewHistoryLoading(true)
    setReviewHistoryError('')
    try {
      const historyUrl = setQueryParams(`${env.urlApi}/admin/ai?resource=dashboard&action=review-history`, { limit: 30 })
      const historyResponse = await getJson(historyUrl, headers, 10000)
      setReviewHistory(extractItems(historyResponse))
    } catch (historyError) {
      console.warn('No se pudo cargar historial de Revision Dev.', historyError)
      setReviewHistoryError(historyError && historyError.message ? historyError.message : 'No se pudo cargar el historial.')
      setReviewHistory([])
    } finally {
      setReviewHistoryLoading(false)
    }
  }

  useEffect(() => {
    async function loadDashboard() {
      let queryToken = firstValue(getQueryValue('token'), getQueryValue('sabreToken'), getQueryValue('ath'))
      if (!queryToken) {
        // Fallback: token Sabre ya guardado en el navegador (Boston IA o sesion del admin)
        try {
          const saved = JSON.parse(window.localStorage.getItem('boston_scripts_session') || '{}')
          queryToken = window.localStorage.getItem('boston_session_token_v1') || saved.sabreToken || ''
        } catch (_) {}
      }
      setToken(queryToken)
      if (queryToken === '') {
        setMessage(makeT(getDashboardLang())('tokenRequired'))
        setMessageIcon('warning')
        setLoading(false)
        return
      }

      const env = getDashboardEnvironment()
      const headers = {
        Accept: 'application/json, text/plain, */*',
        'x-api-key': env.key,
        token: queryToken,
      }

      try {
        try {
          const sessionResponse = await getJson(`${env.urlApi}/customScripts/session`, headers, 10000)
          const runtimeSession = {
            username: firstValue(sessionResponse?.username, sessionResponse?.user),
            parentPcc: firstValue(sessionResponse?.parent_pcc, sessionResponse?.parentPcc, sessionResponse?.parentPCC),
            pcc: firstValue(sessionResponse?.pcc, sessionResponse?.PCC),
          }
          setDashboardRuntimeSession(runtimeSession)

          const permissionUrl = setQueryParams(`${env.urlApi}/user/dashboard-permissions/active`, {
            username: runtimeSession.username,
            parentPcc: runtimeSession.parentPcc,
            pcc: runtimeSession.pcc,
          })
          const permissionResponse = await getJson(permissionUrl, headers, 10000)
          const permissions = permissionResponse?.permissions || {}
          console.debug('dashboard runtimeSession', runtimeSession)
          console.debug('dashboard permissionUrl', permissionUrl)
          console.debug('dashboard-permissions/active response', permissionResponse)
          const normalizedPermissions = {
            enableScriptGenerator: toBool(permissions.enableScriptGenerator ?? permissions.enable_script_generator),
            enableRepositories: toBool(permissions.enableRepositories ?? permissions.enable_repositories),
          }
          console.debug('dashboardPermissions normalized', normalizedPermissions)
          setDashboardPermissions(normalizedPermissions)

          setRepoLoading(true)
          setRepoError('')
          try {
            const repositoryResponse = await getJson(`${env.urlApi}/admin/ai?resource=dashboard&action=repository`, headers, 10000)
            setRepoScripts(extractItems(repositoryResponse))
          } catch (repositoryError) {
            console.warn('No se pudo cargar Repositorio IA.', repositoryError)
            setRepoError(repositoryError && repositoryError.message ? repositoryError.message : 'No se pudo cargar el repositorio.')
          } finally {
            setRepoLoading(false)
          }
          await loadDashboardReviewHistory(headers, env)
        } catch (permissionError) {
          console.warn('No se pudieron cargar permisos dashboard runtime.', permissionError)
          setDashboardRuntimeSession({ username: '', parentPcc: '', pcc: '' })
          setDashboardPermissions({ enableScriptGenerator: false, enableRepositories: false })
          setRepoScripts([])
          setReviewHistory([])
          setRepoLoading(false)
          setRepoError('')
        }

        getJson(`${env.urlApi}/agency/active`, headers, 5000)
          .then(() => setActive(true))
          .catch((error) => {
            console.warn('No se pudo validar agency/active, se intentara cargar scripts de todos modos.', error)
            setActive(false)
          })

        const scriptsResponse = await getJson(`${env.urlApi}/customScripts`, headers, 60000)
        const availableScripts = extractItems(scriptsResponse).filter((script) => {
          return script && typeof script === 'object' && String(script.url || '').trim() !== ''
        })

        if (availableScripts.length === 0) {
          setScripts([])
          setMessage(makeT(getDashboardLang())('noScripts'))
          setMessageIcon('grid')
          setLoading(false)
          return
        }

        setScripts(availableScripts)
        setLoading(false)
      } catch (error) {
        console.error(error)
        setActive(false)
        const msg = error && error.message ? error.message : 'error de red'
        const isAuth = /HTTP 40[13]/.test(msg)
        setMessage(isAuth
          ? `Token Sabre invalido o vencido — ${msg}. Abri el dashboard con un ?token= vigente.`
          : `No se pudieron cargar los scripts. Detalle: ${msg}`)
        setMessageIcon('warning')
        setScripts([])
        setLoading(false)
      }
    }

    loadDashboard()
  }, [reloadTick])

  // La X interna del script (runtime) avisa por postMessage cuando corre embebido:
  // acá cerramos el modal del visor (grid y repositorio).
  useEffect(() => {
    function onScriptMessage(event) {
      if (event && event.data && event.data.type === 'boston-script-close') {
        setRepoPreview(null)
      }
    }
    window.addEventListener('message', onScriptMessage)
    return () => window.removeEventListener('message', onScriptMessage)
  }, [])

  // Mantiene el constructor flotante visible y evita que cubra el pie del dashboard.
  useEffect(() => {
    if (!canUseScriptGenerator) return undefined

    let frameId = 0
    const updateFloatLimit = () => {
      frameId = 0
      const footer = dashboardFooterRef.current
      if (!footer) return

      const footerRect = footer.getBoundingClientRect()
      const footerTop = footerRect.top
      const viewportHeight = window.innerHeight || document.documentElement.clientHeight || 0
      const baseGap = 12
      // Sabre puede recortar el borde inferior del iframe. Reservar siempre la
      // altura real del footer evita que el botón caiga dentro de esa zona al
      // terminar de cargar y enviar el footer fuera del viewport.
      const minimumBottom = Math.ceil(footerRect.height) + baseGap
      const requiredBottom = footerTop < viewportHeight
        ? viewportHeight - footerTop + baseGap
        : minimumBottom
      const nextBottom = Math.max(minimumBottom, Math.min(Math.ceil(requiredBottom), Math.max(minimumBottom, viewportHeight - 64)))
      setIaFloatBottom((current) => (current === nextBottom ? current : nextBottom))
    }
    const scheduleFloatLimit = () => {
      if (frameId) window.cancelAnimationFrame(frameId)
      frameId = window.requestAnimationFrame(updateFloatLimit)
    }

    scheduleFloatLimit()
    window.addEventListener('resize', scheduleFloatLimit)
    window.addEventListener('scroll', scheduleFloatLimit, true)
    const resizeObserver = typeof window.ResizeObserver === 'function'
      ? new window.ResizeObserver(scheduleFloatLimit)
      : null
    if (resizeObserver && dashboardFooterRef.current) resizeObserver.observe(dashboardFooterRef.current)

    return () => {
      if (frameId) window.cancelAnimationFrame(frameId)
      window.removeEventListener('resize', scheduleFloatLimit)
      window.removeEventListener('scroll', scheduleFloatLimit, true)
      if (resizeObserver) resizeObserver.disconnect()
    }
  }, [canUseScriptGenerator])

  function changeSearch(value) {
    setSearch(value)
    setPage(1)
  }

  function changeRepoSearch(value) {
    setRepoSearch(value)
    setRepoPage(1)
  }

  async function distributeRepositoryScript(script) {
    const uuid = String(script?.uuid || '').trim()
    if (uuid === '') {
      setRepoMessage('No se encontro uuid del script.')
      return
    }
    if (String(script?.url || '').trim() === '') {
      setRepoMessage('El script aun no esta publicado.')
      return
    }

    const env = getDashboardEnvironment()
    const headers = {
      Accept: 'application/json, text/plain, */*',
      'Content-Type': 'application/json',
      'x-api-key': env.key,
      token,
    }

    setRepoBusyUuid(uuid)
    setRepoMessage('')
    try {
      await postJson(`${env.urlApi}/admin/ai?resource=dashboard&action=distribute-script`, headers, {
        uuid,
        parent_pcc: dashboardRuntimeSession.parentPcc,
      }, 20000)
      setRepoScripts((items) => items.map((item) => {
        if (String(item.uuid || '').trim() !== uuid) return item
        return {
          ...item,
          distributedToPcc: true,
          canDistribute: true,
          status: 'distribuido',
          statusLabel: 'Distribuido',
        }
      }))
      setRepoMessage('Script distribuido al PCC correctamente.')
      setReloadTick((value) => value + 1)
    } catch (error) {
      console.error('[dashboard repository] distribute error', error)
      setRepoMessage(error && error.message ? error.message : 'No se pudo distribuir el script.')
    } finally {
      setRepoBusyUuid('')
    }
  }

  async function loadDistributionPccs(uuid) {
    const env = getDashboardEnvironment()
    const headers = { Accept: 'application/json, text/plain, */*', 'x-api-key': env.key, token }
    const pccsUrl = setQueryParams(`${env.urlApi}/admin/ai?resource=dashboard&action=pccs`, {
      uuid,
      parent_pcc: dashboardRuntimeSession.parentPcc,
    })
    const pccsRes = await getJson(pccsUrl, headers, 12000)
    const seen = new Set()
    const pccs = extractItems(pccsRes)
      .map((r) => ({
        pcc: String(firstValue(r.pcc, r.PCC, r.pcc_code)).toUpperCase(),
        users: Number(firstValue(r.users, r.count, r.totalUsers) || 0),
        assigned: toBool(firstValue(r.assigned, r.Assigned)),
      }))
      .filter((p) => p.pcc && !seen.has(p.pcc) && seen.add(p.pcc))
    setDistPccs(pccs)
    return pccs
  }

  async function loadDistributionUsers(pcc, uuid) {
    const selected = String(pcc || '').toUpperCase()
    const env = getDashboardEnvironment()
    const headers = { Accept: 'application/json, text/plain, */*', 'x-api-key': env.key, token }
    const usersUrl = setQueryParams(`${env.urlApi}/admin/ai?resource=dashboard&action=users`, {
      uuid,
      parent_pcc: dashboardRuntimeSession.parentPcc,
      pcc: selected,
    })
    const usersRes = await getJson(usersUrl, headers, 12000)
    const loadedUsers = extractItems(usersRes)
      .map((u) => ({
        username: String(firstValue(u.username, u.user, u.usuario)),
        pcc: String(firstValue(u.pcc, u.PCC, u.pcc_code)).toUpperCase(),
        preferredLanguage: String(firstValue(u.preferred_language, u.preferredLanguage)),
        assigned: toBool(firstValue(u.assigned, u.Assigned)),
      }))
      .filter((u) => u.username && u.pcc === selected)
    setDistUsers(loadedUsers)
    setDistSelectedUsers(loadedUsers
      .filter((user) => user.assigned)
      .map((user) => `${user.username}|${user.pcc}`))
    return loadedUsers
  }

  // Abre el modal y carga los PCCs/Usuarios del Parent PCC (token Sabre del dashboard).
  async function openDistributeModal(script) {
    const uuid = String(script?.uuid || '').trim()
    if (!uuid) { setRepoMessage('No se encontro uuid del script.'); return }
    if (String(script?.url || '').trim() === '') { setRepoMessage('El script aun no esta publicado.'); return }
    setDistScript(script)
    setDistSelectedPcc('')
    setDistApplyOnlyPcc(false)
    setDistSelectedUsers([])
    setDistMsg('')
    setDistPccs([])
    setDistUsers([])
    setDistLoading(true)
    try {
      await loadDistributionPccs(uuid)
    } catch (e) {
      setDistMsg(e.message || 'Error cargando PCCs')
    } finally {
      setDistLoading(false)
    }
  }

  async function selectDistPcc(pcc) {
    const selected = String(pcc || '').toUpperCase()
    setDistSelectedPcc(selected)
    setDistApplyOnlyPcc(false)
    setDistSelectedUsers([])
    setDistUsers([])
    setDistMsg('')
    if (!selected) return
    setDistUsersLoading(true)
    try {
      await loadDistributionUsers(selected, String(distScript?.uuid || '').trim())
    } catch (e) {
      setDistMsg(e.message || 'Error cargando usuarios del PCC')
    } finally {
      setDistUsersLoading(false)
    }
  }

  function toggleDistUser(user) {
    const key = `${user.username}|${user.pcc}`
    setDistSelectedUsers((list) => (list.includes(key)
      ? list.filter((item) => item !== key)
      : [...list, key]))
  }
  function closeDistribute() {
    if (distBusy) return
    setDistScript(null)
    setDistMsg('')
    setDistUsersLoading(false)
  }

  function openRepoPreview(script) {
    const url = buildScriptUrl(String(script.url || ''), token, script, advancedMode)
    if (url === '#') { setRepoMessage('El script no tiene URL para previsualizar.'); return }
    setRepoPreview({ name: String(script.name || script.title || script.uuid || 'Script'), url })
  }
  function openPublishedPreview(script) {
    const url = buildScriptUrl(String(script?.url || ''), token, script, advancedMode)
    if (url === '#') { setRepoMessage('El script publicado no tiene URL para previsualizar.'); return }
    setRepoPreview({ name: String(script?.title || script?.name || script?.uuid || 'Script'), url })
  }
  function closeRepoPreview() { setRepoPreview(null) }

  function openReviewHistory() {
    setReviewHistoryOpen(true)
  }

  function closeReviewHistory() {
    setReviewHistoryOpen(false)
  }

  function toggleScriptGenerator() {
    if (iaOpen) {
      setIaOpen(false)
      setScriptGuideOpen(false)
      return
    }
    setIaOpen(true)
    setScriptGuideOpen(true)
  }

  function closeScriptGuide() {
    setScriptGuideOpen(false)
  }

  async function clearReviewNotifications() {
    if (reviewClearBusy) return
    const env = getDashboardEnvironment()
    const headers = { Accept: 'application/json, text/plain, */*', 'Content-Type': 'application/json', 'x-api-key': env.key, token }
    setReviewClearBusy(true)
    setReviewHistoryError('')
    try {
      const unreadReviewIds = reviewHistory
        .filter((item) => reviewUnreadMessageCount(item) > 0)
        .map((item) => String(item.id || '').trim())
        .filter(Boolean)
      await Promise.all(unreadReviewIds.map((reviewId) => postJson(
        `${env.urlApi}/admin/ai?resource=dashboard&action=review-messages-read`,
        headers,
        { reviewId },
        10000,
      ).catch((err) => {
        console.warn('No se pudo marcar mensaje como leido.', reviewId, err)
      })))
      setReviewHistory((items) => items.map((item) => ({
        ...item,
        unreadMessages: 0,
        messages: (item.messages || []).map((message) => (
          String(message.senderType || '').toLowerCase() === 'dev'
            ? { ...message, readByClient: true }
            : message
        )),
      })))
      await postJson(
        `${env.urlApi}/admin/ai?resource=dashboard&action=review-notifications-clear`,
        headers,
        {},
        10000,
      )
      await loadDashboardReviewHistory(headers, env)
    } catch (err) {
      console.warn('No se pudo vaciar historial de Revision Dev.', err)
      setReviewHistoryError(err && err.message ? err.message : 'No se pudieron vaciar las notificaciones.')
    } finally {
      setReviewClearBusy(false)
    }
  }

  async function sendReviewClientReply(item) {
    const reviewId = String(item?.id || '').trim()
    const text = String(reviewReplyDrafts[reviewId] || '').trim()
    if (!reviewId || !text) return
    const env = getDashboardEnvironment()
    const headers = { Accept: 'application/json, text/plain, */*', 'Content-Type': 'application/json', 'x-api-key': env.key, token }
    setReviewReplyBusyId(reviewId)
    try {
      const saved = await postJson(
        `${env.urlApi}/admin/ai?resource=dashboard&action=review-message`,
        headers,
        { reviewId, message: text },
        15000,
      )
      setReviewHistory((items) => items.map((historyItem) => {
        if (String(historyItem.id || '') !== reviewId) return historyItem
        const messages = Array.isArray(historyItem.messages) ? historyItem.messages.slice() : []
        messages.push(saved || {
          id: `client-${Date.now()}`,
          reviewId,
          senderType: 'client',
          message: text,
          readByClient: true,
          readByDev: false,
          createdDate: new Date().toISOString(),
        })
        return { ...historyItem, messages }
      }))
      setReviewReplyDrafts((drafts) => ({ ...drafts, [reviewId]: '' }))
    } catch (err) {
      setReviewHistoryError(err && err.message ? err.message : 'No se pudo enviar la respuesta.')
    } finally {
      setReviewReplyBusyId('')
    }
  }

  // Los scripts del grid se abren en modal (no en otra pestaña): mismo visor que el repo.
  function openScriptModal(script) {
    const url = buildScriptUrl(String(script.url || ''), token, script, advancedMode)
    if (url === '#') { return }
    setRepoPreview({ name: String(script.name || script.namescript || 'Script'), url })
  }

  // Asigna el script a cada PCC y Usuario seleccionado (mismos endpoints que el admin).
  async function confirmDistribute() {
    if (!distScript) return
    if (!distSelectedPcc) { setDistMsg('Selecciona un PCC.'); return }
    if (!distApplyOnlyPcc && !selectedUsersForPcc.length) { setDistMsg('Selecciona al menos un usuario.'); return }
    const uuid = String(distScript.uuid || '').trim()
    const env = getDashboardEnvironment()
    const headers = { Accept: 'application/json, text/plain, */*', 'Content-Type': 'application/json', 'x-api-key': env.key, token }
    const pccs = distApplyOnlyPcc ? [distSelectedPcc] : []
    const users = distApplyOnlyPcc ? [] : selectedUsersForPcc.map((u) => ({ username: u.username, pcc: u.pcc }))
    setDistBusy(true); setDistMsg('')
    try {
      await postJson(`${env.urlApi}/admin/ai?resource=dashboard&action=distribute-script`, headers, {
        uuid,
        parent_pcc: dashboardRuntimeSession.parentPcc,
        pccs,
        users,
      }, 30000)
      await loadDistributionPccs(uuid)
      if (distSelectedPcc) {
        await loadDistributionUsers(distSelectedPcc, uuid)
      }
      setDistMsg('Distribucion aplicada correctamente.')
      setRepoMessage('Distribucion aplicada correctamente.')
    } catch (e) {
      setDistMsg(e.message || 'No se pudo distribuir.')
    } finally {
      setDistBusy(false)
    }
  }

  function openDeleteModal(script) {
    const uuid = String(script?.uuid || '').trim()
    if (!uuid) { setRepoMessage('No se encontro uuid del script.'); return }
    setDeleteMsg('')
    setDeleteTarget(script)
  }

  function closeDeleteModal() {
    if (deleteBusy) return
    setDeleteTarget(null)
    setDeleteMsg('')
  }

  async function resendToDev(script) {
    // La confirmación vive en el modal propio (resendTarget): window.confirm NO
    // funciona dentro del iframe de Sabre Red Web (retorna false sin mostrarse).
    const uuid = String(script.uuid || '').trim()
    if (!uuid || resendBusyUuid) return
    const displayName = humanizeScriptName(String(script.name || script.title || uuid))
    const env = getDashboardEnvironment()
    const headers = { Accept: 'application/json, text/plain, */*', 'Content-Type': 'application/json', 'x-api-key': env.key, token }
    setResendBusyUuid(uuid)
    setRepoMessage('')
    try {
      await postJson(`${env.urlApi}/admin/ai?resource=dashboard&action=resend-review`, headers, { uuid, parent_pcc: dashboardRuntimeSession.parentPcc, feedback: String(resendFeedback || '').trim() }, 30000)
      setRepoMessage(t('resendOk', { name: displayName }))
      setReloadTick((value) => value + 1)
    } catch (e) {
      setRepoMessage(e.message || 'No se pudo reenviar a Dev.')
    } finally {
      setResendBusyUuid('')
      setResendTarget(null)
      setResendFeedback('')
    }
  }

  async function confirmDeleteScript() {
    if (!deleteTarget || deleteBusy) return
    const uuid = String(deleteTarget.uuid || '').trim()
    const displayName = String(deleteTarget.name || deleteTarget.title || uuid)
    const env = getDashboardEnvironment()
    const headers = { Accept: 'application/json, text/plain, */*', 'Content-Type': 'application/json', 'x-api-key': env.key, token }
    setDeleteBusy(true)
    setDeleteMsg('')
    try {
      await postJson(`${env.urlApi}/admin/ai?resource=dashboard&action=delete-script`, headers, {
        uuid,
        parent_pcc: dashboardRuntimeSession.parentPcc,
      }, 30000)
      setRepoScripts((items) => items.filter((item) => String(item.uuid || '').trim() !== uuid))
      setRepoMessage(`«${displayName}» fue eliminado del repositorio.`)
      setDeleteTarget(null)
      setReloadTick((value) => value + 1)
    } catch (e) {
      setDeleteMsg(e.message || 'No se pudo eliminar el script.')
    } finally {
      setDeleteBusy(false)
    }
  }

  const visiblePages = pageWindow(currentPage, totalPages)
  const visibleRepoPages = pageWindow(currentRepoPage, repoTotalPages)
  const showGrid = !loading && scripts.length > 0

  return (
    <main className="dashboard-shell">
      <header className="dashboard-header">
        <div className="dashboard-brand">
          <img
            className="dashboard-logo"
            src={`${process.env.PUBLIC_URL || ''}/bf_large.png`}
            alt="boston"
          />
          <span className="dashboard-ai" title="Nueva version con IA y agente">
            <DashboardIcon name="sparkle" /> IA
          </span>
        </div>
        <div className="dashboard-header-right">
          <div className="dashboard-lang dashboard-mode" role="group" aria-label="Modo de script">
            <button type="button" className={`dashboard-lang-btn ${advancedMode ? '' : 'active'}`} onClick={() => changeAdvancedMode(false)} title={lang === 'en' ? 'Simple mode' : 'Modo simple'}>
              Simple
            </button>
            <button type="button" className={`dashboard-lang-btn ${advancedMode ? 'active' : ''}`} onClick={() => changeAdvancedMode(true)} title={lang === 'en' ? 'Advanced mode' : 'Modo avanzado'}>
              {lang === 'en' ? 'Advanced' : 'Avanzado'}
            </button>
          </div>
          <button
            type="button"
            className={`dashboard-notification-btn ${reviewNotificationCount > 0 ? 'has-news' : ''}`}
            onClick={openReviewHistory}
            title={t('reviewHistoryTitle')}
            aria-label={t('reviewHistoryTitle')}
            aria-busy={reviewHistoryLoading ? 'true' : 'false'}
          >
            <DashboardIcon name="bell" />
            {reviewNotificationCount > 0 ? <span className="dashboard-notification-count">{reviewNotificationCount}</span> : null}
          </button>
          <div className="dashboard-lang" role="group" aria-label="Idioma / Language">
            <button type="button" className={`dashboard-lang-btn ${lang === 'es' ? 'active' : ''}`} onClick={() => changeLang('es')} title="Español">
              ES
            </button>
            <button type="button" className={`dashboard-lang-btn ${lang === 'en' ? 'active' : ''}`} onClick={() => changeLang('en')} title="English">
              EN
            </button>
          </div>
          {active ? <span className="dashboard-badge"><DashboardIcon name="check" /> {t('agencyActive')}</span> : null}
        </div>
      </header>

      <div className={`dashboard-body ${canUseRepositories ? 'has-repositories' : ''} ${canUseScriptGenerator ? 'has-float' : ''}`.trim()}>
        <section className="dashboard-main custom-scripts-panel">
          {!showGrid ? (
            <div className="dashboard-empty">
              <DashboardIcon name={messageIcon} />
              <p>{message}</p>
            </div>
          ) : (
            <>
              <div className="dashboard-toolbar">
                <div className="dashboard-search">
                  <DashboardIcon name="search" />
                  <input
                    type="text"
                    placeholder={t('searchPlaceholder')}
                    value={search}
                    onChange={(event) => changeSearch(event.target.value)}
                  />
                  {search !== '' ? (
                    <button type="button" className="dashboard-search-clear" onClick={() => changeSearch('')} aria-label={t('clearSearch')}>×</button>
                  ) : null}
                </div>
                <span className="dashboard-count">
                  {filtered.length} {filtered.length === 1 ? t('script') : t('scripts')}
                </span>
              </div>

              {filtered.length === 0 ? (
                <div className="dashboard-empty soft">
                  <DashboardIcon name="grid" />
                  <p>{t('noResults', { q: search })}</p>
                </div>
              ) : (
                <div className="dashboard-grid">
                  {pageItems.map((script, index) => {
                    const rawTitle = String(script.name || script.namescript || 'Script')
                    const title = humanizeScriptName(rawTitle)
                    const visual = scriptVisual(script, rawTitle)
                    const cardKey = `card|${script.uuid || rawTitle}`
                    const desc = String(script.description || '').trim()
                    const descExpanded = expandedDescs.includes(cardKey)
                    return (
                      <button
                        type="button"
                        className="dashboard-card"
                        data-index={index}
                        key={`${script.uuid || rawTitle}-${index}`}
                        onClick={() => openScriptModal(script)}
                        title={t('openScript', { name: title })}
                      >
                        <span className={`dashboard-icon dc-${visual.color}`}><DashboardIcon name={visual.icon} /></span>
                        <strong>{title}</strong>
                        {desc ? (
                          <span className={`dashboard-card-desc ${descExpanded ? 'expanded' : ''}`}>
                            {desc}
                          </span>
                        ) : null}
                        {desc.length > 60 ? (
                          <span
                            role="button"
                            tabIndex={0}
                            className="dashboard-readmore"
                            onClick={(event) => { event.stopPropagation(); toggleDesc(cardKey) }}
                            onKeyDown={(event) => { if (event.key === 'Enter') { event.stopPropagation(); toggleDesc(cardKey) } }}
                          >
                            {descExpanded ? t('readLess') : t('readMore')}
                          </span>
                        ) : null}
                        <span
                          role="button"
                          tabIndex={0}
                          className="dashboard-card-config"
                          title={lang === 'en' ? 'User configuration' : 'Configuracion de usuario'}
                          aria-label={lang === 'en' ? 'User configuration' : 'Configuracion de usuario'}
                          onClick={(event) => { event.stopPropagation(); setConfigScript(script) }}
                          onKeyDown={(event) => {
                            if (event.key === 'Enter' || event.key === ' ') {
                              event.preventDefault()
                              event.stopPropagation()
                              setConfigScript(script)
                            }
                          }}
                        >
                          <DashboardIcon name="gear" />
                        </span>
                      </button>
                    )
                  })}
                </div>
              )}

              {totalPages > 1 ? (
                <nav className="dashboard-pager" aria-label="Paginacion de scripts">
                  <button
                    type="button"
                    className="dashboard-pager-btn"
                    disabled={currentPage === 1}
                    onClick={() => setPage(currentPage - 1)}
                    aria-label="Anterior"
                  >
                    <DashboardIcon name="chevron-left" />
                  </button>
                  {visiblePages[0] > 1 ? (
                    <>
                      <button type="button" className="dashboard-pager-btn" onClick={() => setPage(1)}>1</button>
                      {visiblePages[0] > 2 ? <span className="dashboard-pager-gap">…</span> : null}
                    </>
                  ) : null}
                  {visiblePages.map((p) => (
                    <button
                      type="button"
                      key={p}
                      className={`dashboard-pager-btn ${p === currentPage ? 'active' : ''}`}
                      onClick={() => setPage(p)}
                    >
                      {p}
                    </button>
                  ))}
                  {visiblePages[visiblePages.length - 1] < totalPages ? (
                    <>
                      {visiblePages[visiblePages.length - 1] < totalPages - 1 ? <span className="dashboard-pager-gap">…</span> : null}
                      <button type="button" className="dashboard-pager-btn" onClick={() => setPage(totalPages)}>{totalPages}</button>
                    </>
                  ) : null}
                  <button
                    type="button"
                    className="dashboard-pager-btn"
                    disabled={currentPage === totalPages}
                    onClick={() => setPage(currentPage + 1)}
                    aria-label="Siguiente"
                  >
                    <DashboardIcon name="chevron" />
                  </button>
                </nav>
              ) : null}
            </>
          )}
        </section>

        {canUseRepositories ? (
          <aside className="dashboard-repo" aria-busy={repoLoading ? 'true' : 'false'}>
            <h3 className="dashboard-repo-title">{t('repoTitle')} <span className="dashboard-row-type">IA</span></h3>
            {repoLoading && repoScripts.length > 0 ? (
              <div className="dashboard-repo-refresh">
                <DashboardLoader text={t('loadingList')} size="sm" inline />
              </div>
            ) : null}
            {repoMessage ? <p className="dashboard-row-type">{repoMessage}</p> : null}
            {repoLoading && repoScripts.length === 0 ? (
              <div className="dashboard-empty soft">
                <DashboardLoader text={t('loadingList')} />
              </div>
            ) : repoError && repoScripts.length === 0 ? (
              <div className="dashboard-empty soft err">
                <DashboardIcon name="warning" />
                <p>{repoError}</p>
              </div>
            ) : repoScripts.length ? (
              <>
                <div className="dashboard-toolbar dashboard-repo-toolbar">
                  <div className="dashboard-search dashboard-repo-search">
                    <DashboardIcon name="search" />
                    <input
                      type="text"
                      placeholder={t('repoSearchPlaceholder')}
                      value={repoSearch}
                      onChange={(event) => changeRepoSearch(event.target.value)}
                    />
                    {repoSearch !== '' ? (
                      <button type="button" className="dashboard-search-clear" onClick={() => changeRepoSearch('')} aria-label={t('clearSearch')}>×</button>
                    ) : null}
                  </div>
                  <span className="dashboard-count">
                    {filteredRepoScripts.length} {filteredRepoScripts.length === 1 ? t('script') : t('scripts')}
                  </span>
                </div>
                {filteredRepoScripts.length === 0 ? (
                  <div className="dashboard-empty soft">
                    <DashboardIcon name="grid" />
                    <p>{t('repoNoResults', { q: repoSearch })}</p>
                  </div>
                ) : (
                  <div className="dashboard-list">
                    {repoPageItems.map((script) => {
                  const uuid = String(script.uuid || '').trim()
                  const hasUrl = String(script.url || '').trim() !== ''
                  const distributed = toBool(script.distributedToPcc)
                  const statusNorm = String(script.status || script.statusLabel || '').toLowerCase().replace(/[\s-]/g, '_')
                  const inReview = statusNorm === 'en_revision' || statusNorm === 'en_revisión'
                  const canDistribute = hasUrl && !inReview // en revisión: el dev lo tiene, no se distribuye
                  const busy = repoBusyUuid === uuid
                  const rawName = String(script.name || script.title || script.uuid || 'Script IA')
                  const displayName = humanizeScriptName(rawName)
                  const rawStatus = String(script.statusLabel || 'Pendiente')
                  const statusLabel = lang === 'en'
                    ? ({ Pendiente: 'Pending', Distribuido: 'Distributed', Publicado: 'Published', Activado: 'Active', 'En revisión': 'In review', 'En revision': 'In review' }[rawStatus] || rawStatus)
                    : rawStatus
                  const visual = scriptVisual(script, rawName)
                  const repoKey = `repo|${uuid || rawName}`
                  const reviewedReady = reviewedReadyScripts.has(`uuid:${reviewHistoryKey(uuid)}`) ||
                    reviewedReadyScripts.has(`title:${reviewHistoryKey(rawName)}`) ||
                    reviewedReadyScripts.has(`title:${reviewHistoryKey(displayName)}`)
                  const descText = String(script.description || script.feedback || '').trim()
                  const descExpanded = expandedDescs.includes(repoKey)
                  const descShort = descText.length > 90 ? `${descText.slice(0, 90).trim()}… ` : descText
                  const createdTxt = script.createdDate ? `${t('createdAt')} ${formatDateShort(script.createdDate, lang)}` : ''
                  const updatedTxt = script.updatedDate ? `${t('updatedAt')} ${formatDateShort(script.updatedDate, lang)}` : ''
                  return (
                    <div className={`dashboard-row ${reviewedReady ? 'reviewed-ready' : ''}`} key={script.uuid || script.name}>
                      <span className="dashboard-row-main">
                        <span className={`dashboard-icon dc-${visual.color}`}><DashboardIcon name={visual.icon} /></span>
                        <span className="dashboard-row-content">
                          <span className="dashboard-row-name" title={displayName}>
                            <span className="dashboard-row-name-text">{displayName}</span>
                          </span>
                          <span className="dashboard-row-type" title={statusLabel}>{statusLabel}</span>
                          {descText ? (
                            <span className="dashboard-row-desc">
                              {descExpanded ? descText : descShort}
                              {descText.length > 90 ? (
                                <button type="button" className="dashboard-readmore" onClick={() => toggleDesc(repoKey)}>
                                  {descExpanded ? t('readLess') : t('readMore')}
                                </button>
                              ) : null}
                            </span>
                          ) : null}
                          {createdTxt || updatedTxt ? (
                            <span className="dashboard-row-dates">
                              {createdTxt}
                              {createdTxt && updatedTxt ? ' · ' : ''}
                              {updatedTxt}
                            </span>
                          ) : null}
                        </span>
                      </span>
                      <span className="dashboard-row-actions">
                        {hasUrl ? (
                          <button type="button" className="dashboard-row-btn" onClick={() => openRepoPreview(script)} title={t('preview')}>
                            {t('view')}
                          </button>
                        ) : null}
                        <button
                          type="button"
                          className="dashboard-row-btn dashboard-row-icon-btn"
                          onClick={() => setConfigScript(script)}
                          title={lang === 'en' ? 'User configuration' : 'Configuracion de usuario'}
                          aria-label={lang === 'en' ? 'User configuration' : 'Configuracion de usuario'}
                        >
                          <DashboardIcon name="gear" />
                        </button>
                        <button
                          type="button"
                          className="dashboard-row-btn"
                          disabled={!canDistribute || busy}
                          aria-busy={busy ? 'true' : 'false'}
                          onClick={() => openDistributeModal(script)}
                        >
                          {busy ? <span className="dashboard-btn-spinner" aria-hidden="true" /> : null}
                          {busy ? t('distributing') : distributed ? t('redistribute') : t('distribute')}
                        </button>
                        {hasUrl && !inReview ? (
                          <button
                            type="button"
                            className="dashboard-row-btn"
                            disabled={resendBusyUuid === uuid}
                            onClick={() => setResendTarget(script)}
                            title={t('resendDev')}
                          >
                            {resendBusyUuid === uuid ? t('resendBusy') : t('resendDev')}
                          </button>
                        ) : inReview ? (
                          <span className="dashboard-row-type" style={{ alignSelf: 'center', color: '#b45309', fontWeight: 600 }} title="El script está en Revisión Dev">
                            {lang === 'en' ? 'With Dev' : 'Con el Dev'}
                          </span>
                        ) : null}
                        <button
                          type="button"
                          className="dashboard-row-btn danger"
                          disabled={busy || deleteBusy}
                          onClick={() => openDeleteModal(script)}
                          title={t('deleteConfirm')}
                        >
                          {t('remove')}
                        </button>
                      </span>
                    </div>
                  )
                    })}
                  </div>
                )}
                {repoTotalPages > 1 ? (
                  <nav className="dashboard-pager dashboard-repo-pager" aria-label={lang === 'en' ? 'Repository pagination' : 'Paginacion de repositorios'}>
                    <button
                      type="button"
                      className="dashboard-pager-btn"
                      disabled={currentRepoPage === 1}
                      onClick={() => setRepoPage(currentRepoPage - 1)}
                      aria-label={lang === 'en' ? 'Previous' : 'Anterior'}
                    >
                      <DashboardIcon name="chevron-left" />
                    </button>
                    {visibleRepoPages[0] > 1 ? (
                      <>
                        <button type="button" className="dashboard-pager-btn" onClick={() => setRepoPage(1)}>1</button>
                        {visibleRepoPages[0] > 2 ? <span className="dashboard-pager-gap">...</span> : null}
                      </>
                    ) : null}
                    {visibleRepoPages.map((p) => (
                      <button
                        type="button"
                        key={p}
                        className={`dashboard-pager-btn ${p === currentRepoPage ? 'active' : ''}`}
                        onClick={() => setRepoPage(p)}
                      >
                        {p}
                      </button>
                    ))}
                    {visibleRepoPages[visibleRepoPages.length - 1] < repoTotalPages ? (
                      <>
                        {visibleRepoPages[visibleRepoPages.length - 1] < repoTotalPages - 1 ? <span className="dashboard-pager-gap">...</span> : null}
                        <button type="button" className="dashboard-pager-btn" onClick={() => setRepoPage(repoTotalPages)}>{repoTotalPages}</button>
                      </>
                    ) : null}
                    <button
                      type="button"
                      className="dashboard-pager-btn"
                      disabled={currentRepoPage === repoTotalPages}
                      onClick={() => setRepoPage(currentRepoPage + 1)}
                      aria-label={lang === 'en' ? 'Next' : 'Siguiente'}
                    >
                      <DashboardIcon name="chevron" />
                    </button>
                  </nav>
                ) : null}
              </>
            ) : (
              <div className="dashboard-empty soft">
                <DashboardIcon name="grid" />
                <p>{t('repoEmpty')}</p>
              </div>
            )}
            {repoError && repoScripts.length > 0 ? (
              <p className="dashboard-row-type dashboard-repo-error">{repoError}</p>
            ) : null}
          </aside>
        ) : null}
      </div>

      <footer className="dashboard-footer" ref={dashboardFooterRef}>
        <div className="dashboard-footer-brand">
          <DashboardIcon name="sparkle" />
          <span>{t('footerPowered')}</span>
        </div>
        <span className="dashboard-footer-copy">© {new Date().getFullYear()} Basset · Boston</span>
      </footer>

      {canUseScriptGenerator ? createPortal((
        <div
          className={`ia-float ${iaOpen ? 'open' : ''}`}
          style={{ '--ia-float-bottom': `${iaFloatBottom}px` }}
        >
          {iaOpen ? (
            <div className="ia-float-panel" role="region" aria-label="Constructor de scripts con IA">
              <BostonIAPanel
                runtimeSession={dashboardRuntimeSession}
                runtimeToken={token}
                runtimeApiBaseUrl={getDashboardEnvironment().urlApi}
                runtimeApiKey={getDashboardEnvironment().key}
                onPublished={() => setReloadTick((value) => value + 1)}
                onOpenPublished={openPublishedPreview}
                onReviewSent={() => setReloadTick((value) => value + 1)}
                t={t}
                lang={lang}
                advancedMode={advancedMode}
              />
            </div>
          ) : null}
          <button
            type="button"
            className={`ia-float-toggle ${iaOpen ? 'open' : ''}`}
            onClick={toggleScriptGenerator}
            aria-expanded={iaOpen ? 'true' : 'false'}
          >
            <DashboardIcon name="sparkle" />
            {iaOpen ? t('floatClose') : t('floatOpen')}
            <span className={`ia-float-caret ${iaOpen ? 'open' : ''}`} aria-hidden="true">▾</span>
          </button>
        </div>
      ), document.body) : null}

      {scriptGuideOpen ? (
        <div
          className="dashboard-modal-overlay dashboard-script-guide-overlay"
          role="dialog"
          aria-modal="true"
          aria-labelledby="dashboard-script-guide-title"
          onClick={closeScriptGuide}
        >
          <div className="dashboard-script-guide-modal" onClick={(event) => event.stopPropagation()}>
            <div className="dashboard-script-guide-head">
              <div>
                <strong id="dashboard-script-guide-title">Cómo pedir un script</strong>
                <span>Mientras más claro sea el pedido, mejor interpreta la IA lo que necesitas construir.</span>
              </div>
              <button type="button" className="dashboard-script-guide-close" aria-label="Cerrar" onClick={closeScriptGuide}>×</button>
            </div>
            <div className="dashboard-script-guide-body">
              <section className="dashboard-script-guide-column good">
                <h3>Solicitud correcta</h3>
                {SCRIPT_REQUEST_GUIDE.good.map((item) => (
                  <article className="dashboard-script-guide-example" key={item.title}>
                    <strong>{item.title}</strong>
                    <p>{item.text}</p>
                  </article>
                ))}
              </section>
              <section className="dashboard-script-guide-column bad">
                <h3>Solicitud incorrecta</h3>
                {SCRIPT_REQUEST_GUIDE.bad.map((item) => (
                  <article className="dashboard-script-guide-example" key={item.title}>
                    <strong>{item.title}</strong>
                    <p>{item.text}</p>
                  </article>
                ))}
              </section>
            </div>
            <div className="dashboard-script-guide-footer">
              <button type="button" className="dashboard-row-btn primary" onClick={closeScriptGuide}>Continuar</button>
            </div>
          </div>
        </div>
      ) : null}

      {reviewHistoryOpen ? (
        <div
          className="dashboard-modal-overlay dashboard-history-overlay"
          role="dialog"
          aria-modal="true"
          onClick={closeReviewHistory}
        >
          <div className="dashboard-history-modal" onClick={(event) => event.stopPropagation()}>
            <div className="dashboard-history-head">
              <div>
                <strong>{t('reviewHistoryTitle')}</strong>
                <span>{t('reviewHistorySubtitle')}</span>
              </div>
              <div className="dashboard-history-actions">
                <button type="button" className="dashboard-row-btn muted" disabled={reviewClearBusy} onClick={clearReviewNotifications}>
                  {reviewClearBusy ? 'Vaciando...' : 'Vaciar'}
                </button>
                <button type="button" className="dashboard-row-btn" onClick={closeReviewHistory}>{t('close')}</button>
              </div>
            </div>
            {reviewHistoryLoading ? (
              <div className="dashboard-empty soft"><DashboardLoader text={t('loadingList')} size="sm" inline /></div>
            ) : reviewHistoryError ? (
              <div className="dashboard-empty soft err">
                <DashboardIcon name="warning" />
                <p>{reviewHistoryError}</p>
              </div>
            ) : visibleReviewHistory.length ? (
              <div className="dashboard-history-list">
                {visibleReviewHistory.map((item) => {
                  const title = humanizeScriptName(String(item.title || item.name || item.scriptUuid || 'Script'))
                  const status = reviewStatusLabel(item, lang)
                  const ready = reviewIsReady(item)
                  const dateText = formatDateShort(item.updatedDate || item.createdDate, lang)
                  const messages = Array.isArray(item.messages) ? item.messages : []
                  const reviewId = String(item.id || '')
                  const unreadMessages = reviewUnreadMessageCount(item)
                  const canUseReviewFeedback = !ready
                  return (
                    <div className={`dashboard-history-item ${ready ? 'ready' : ''} ${unreadMessages > 0 ? 'has-message' : ''}`} key={item.id || `${item.scriptUuid}-${item.createdDate}`}>
                      <div className="dashboard-history-main">
                        <span className="dashboard-history-title" title={title}>{title}</span>
                        <span className="dashboard-history-date">{dateText || (lang === 'en' ? 'No date' : 'Sin fecha')}</span>
                        {canUseReviewFeedback && item.feedback ? <span className="dashboard-history-feedback">{item.feedback}</span> : null}
                        {canUseReviewFeedback && messages.length ? (
                          <div className="dashboard-history-messages">
                            {messages.map((message) => {
                              const fromDev = String(message.senderType || '').toLowerCase() === 'dev'
                              const messageDate = message.createdDate ? formatDateShort(message.createdDate, lang) : ''
                              return (
                                <div className={`dashboard-history-message ${fromDev ? 'from-dev' : 'from-client'}`} key={message.id || `${message.senderType}-${message.createdDate}`}>
                                  <span className="dashboard-history-message-meta">
                                    {fromDev ? 'Dev' : 'Cliente'}{messageDate ? ` · ${messageDate}` : ''}
                                  </span>
                                  <span className="dashboard-history-message-text">{message.message}</span>
                                </div>
                              )
                            })}
                          </div>
                        ) : null}
                        {canUseReviewFeedback ? (
                          <div className="dashboard-history-reply">
                            <textarea
                              value={reviewReplyDrafts[reviewId] || ''}
                              onChange={(event) => setReviewReplyDrafts((drafts) => ({ ...drafts, [reviewId]: event.target.value }))}
                              placeholder="Responder al desarrollador..."
                            />
                            <button
                              type="button"
                              className="dashboard-row-btn"
                              disabled={reviewReplyBusyId === reviewId || !String(reviewReplyDrafts[reviewId] || '').trim()}
                              onClick={() => sendReviewClientReply(item)}
                            >
                              Responder
                            </button>
                          </div>
                        ) : null}
                      </div>
                      <span className={`dashboard-history-status status-${String(item.estado || '').toLowerCase()}`}>
                        {status}
                      </span>
                    </div>
                  )
                })}
              </div>
            ) : (
              <div className="dashboard-empty soft">
                <DashboardIcon name="grid" />
                <p>{t('reviewHistoryEmpty')}</p>
              </div>
            )}
          </div>
        </div>
      ) : null}

      {resendTarget ? (
        <div
          className="dashboard-modal-overlay"
          role="dialog"
          aria-modal="true"
          style={{ position: 'fixed', inset: 0, zIndex: 10001, background: 'rgba(15,23,42,.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 18 }}
          onClick={() => { if (!resendBusyUuid) setResendTarget(null) }}
        >
          <div onClick={(event) => event.stopPropagation()} style={{ width: 'min(440px, 94vw)', background: '#fff', borderRadius: 12, boxShadow: '0 24px 64px rgba(15,23,42,.35)', overflow: 'hidden' }}>
            <div style={{ padding: '14px 16px', borderBottom: '1px solid #e5e7eb' }}>
              <strong>{t('resendDev')}</strong>
            </div>
            <div style={{ padding: 16 }}>
              <p style={{ margin: 0, fontSize: 13, color: '#334155' }}>
                {t('resendConfirm', { name: humanizeScriptName(String(resendTarget.name || resendTarget.title || resendTarget.uuid)) })}
              </p>
              <label style={{ display: 'block', marginTop: 12, fontSize: 12.5, fontWeight: 600, color: '#334155' }}>
                {lang === 'en' ? 'Reason (the dev will see this)' : 'Motivo del reenvío (lo verá el dev)'}
              </label>
              <textarea
                value={resendFeedback}
                onChange={(event) => setResendFeedback(event.target.value)}
                rows={3}
                maxLength={500}
                placeholder={lang === 'en' ? 'E.g.: validation fails on the date field…' : 'Ej: la validación de la fecha falla, corregir el formato DDMMM…'}
                style={{ width: '100%', marginTop: 6, padding: '8px 10px', border: '1px solid #cbd5e1', borderRadius: 8, fontSize: 13, resize: 'vertical', boxSizing: 'border-box' }}
                disabled={Boolean(resendBusyUuid)}
              />
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, padding: 14, borderTop: '1px solid #e5e7eb' }}>
              <button type="button" className="dashboard-row-btn" onClick={() => setResendTarget(null)} disabled={Boolean(resendBusyUuid)}>{t('cancel')}</button>
              <button
                type="button"
                className="dashboard-row-btn"
                style={{ background: '#22d989', borderColor: '#22d989', color: '#0f172a', fontWeight: 700 }}
                onClick={() => resendToDev(resendTarget)}
                disabled={Boolean(resendBusyUuid)}
              >
                {resendBusyUuid ? t('resendBusy') : t('resendDev')}
              </button>
            </div>
          </div>
        </div>
      ) : null}
      {deleteTarget ? (
        <div
          className="dashboard-modal-overlay"
          role="dialog"
          aria-modal="true"
          style={{ position: 'fixed', inset: 0, zIndex: 10001, background: 'rgba(15,23,42,.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 18 }}
          onClick={closeDeleteModal}
        >
          <div onClick={(event) => event.stopPropagation()} style={{ width: 'min(440px, 94vw)', background: '#fff', borderRadius: 12, boxShadow: '0 24px 64px rgba(15,23,42,.35)', overflow: 'hidden' }}>
            <div style={{ padding: '14px 16px', borderBottom: '1px solid #e5e7eb' }}>
              <strong>{t('deleteTitle', { name: humanizeScriptName(String(deleteTarget.name || deleteTarget.title || deleteTarget.uuid)) })}</strong>
            </div>
            <div style={{ padding: 16 }}>
              <p style={{ margin: 0, fontSize: 13, color: '#334155' }}>
                {t('deleteBody', { pcc: dashboardRuntimeSession.parentPcc || '—' })}
              </p>
              <p style={{ margin: '8px 0 0', fontSize: 12, color: '#b91c1c', fontWeight: 600 }}>
                {t('deleteIrreversible')}
              </p>
              {deleteMsg ? <p style={{ margin: '10px 0 0', fontSize: 12, color: '#b91c1c' }}>{deleteMsg}</p> : null}
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, padding: 14, borderTop: '1px solid #e5e7eb' }}>
              <button type="button" className="dashboard-row-btn" onClick={closeDeleteModal} disabled={deleteBusy}>{t('cancel')}</button>
              <button
                type="button"
                className="dashboard-row-btn danger solid"
                onClick={confirmDeleteScript}
                disabled={deleteBusy}
                aria-busy={deleteBusy ? 'true' : 'false'}
              >
                {deleteBusy ? <span className="dashboard-btn-spinner" aria-hidden="true" /> : null}
                {deleteBusy ? t('deleting') : t('deleteConfirm')}
              </button>
            </div>
          </div>
        </div>
      ) : null}
      {distScript ? (
        <div
          className="dashboard-modal-overlay"
          role="dialog"
          aria-modal="true"
          style={{ position: 'fixed', inset: 0, zIndex: 10000, background: 'rgba(15,23,42,.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 18 }}
          onClick={closeDistribute}
        >
          <div onClick={(event) => event.stopPropagation()} style={{ width: 'min(880px, 96vw)', maxHeight: '90vh', overflow: 'auto', background: '#fff', borderRadius: 12, boxShadow: '0 24px 64px rgba(15,23,42,.35)', padding: 20 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, marginBottom: 6 }}>
              <h3 style={{ margin: 0 }}>{t('distTitle', { name: humanizeScriptName(String(distScript.name || distScript.title || distScript.uuid)) })}</h3>
              <button type="button" className="dashboard-row-btn" onClick={closeDistribute} disabled={distBusy}>{t('close')}</button>
            </div>
            <p style={{ fontSize: 12, color: '#64748b', marginTop: 0 }}>
              {t('distHelp', { pcc: dashboardRuntimeSession.parentPcc || '—' })}
            </p>
            {distLoading ? (
              <div className="dashboard-empty soft"><DashboardLoader text={t('loadingPccs')} /></div>
            ) : (
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                <div>
                  <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 8 }}>{t('pccs')} ({distPccs.length})</div>
                  <div style={{ maxHeight: '42vh', overflow: 'auto', border: '1px solid #e5e7eb', borderRadius: 8 }}>
                    {distPccs.length ? distPccs.map((p) => (
                      <button
                        key={p.pcc}
                        type="button"
                        onClick={() => selectDistPcc(p.pcc)}
                        style={{
                          width: '100%',
                          display: 'grid',
                          gridTemplateColumns: 'minmax(0, 1fr) auto auto',
                          gap: 8,
                          alignItems: 'center',
                          padding: '9px 10px',
                          border: 0,
                          borderBottom: '1px solid #f1f5f9',
                          background: distSelectedPcc === p.pcc ? '#e0f2fe' : '#fff',
                          color: distSelectedPcc === p.pcc ? '#075985' : '#0f172a',
                          cursor: 'pointer',
                          fontSize: 13,
                          fontWeight: distSelectedPcc === p.pcc ? 700 : 500,
                          textAlign: 'left',
                        }}
                      >
                        <span style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.pcc}</span>
                        {p.users ? <span style={{ color: '#64748b', fontSize: 12 }}>{p.users} {t('usersCount')}</span> : null}
                        {p.assigned ? <span style={{ color: '#047857', fontSize: 11, fontWeight: 700 }}>{t('assigned')}</span> : null}
                      </button>
	                    )) : <div style={{ padding: 12, fontSize: 12, color: '#94a3b8' }}>{t('noPccs')}</div>}
	                  </div>
                  <label style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 10, fontSize: 13, color: '#334155' }}>
                    <input
                      type="checkbox"
                      checked={distApplyOnlyPcc}
                      onChange={(event) => setDistApplyOnlyPcc(event.target.checked)}
                      disabled={!distSelectedPcc || distBusy}
                    />
                    {t('onlyPcc')}
                  </label>
	                </div>
	                <div>
	                  <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 8 }}>
                    {t('users')} ({distSelectedPcc ? (distApplyOnlyPcc ? usersForSelectedPcc.length : selectedUsersForPcc.length) : 0})
                  </div>
                  {distSelectedPcc ? (
                    <p style={{ fontSize: 12, color: '#64748b', margin: '0 0 8px' }}>
                      {distApplyOnlyPcc ? t('onlyPccHelp') : t('pickUsersHelp')}
                    </p>
                  ) : null}
                  {distSelectedPcc && !distApplyOnlyPcc && !distUsersLoading && usersForSelectedPcc.length > 0 && selectedUsersForPcc.length === 0 ? (
                    <p style={{ fontSize: 12, color: '#b45309', margin: '0 0 8px' }}>{t('pickAtLeastOne')}</p>
                  ) : null}
                  <div style={{ maxHeight: '42vh', overflow: 'auto', border: '1px solid #e5e7eb', borderRadius: 8 }}>
                    {distUsersLoading ? (
                      <div className="dashboard-empty soft"><DashboardLoader text={t('loadingUsers')} size="sm" inline /></div>
                    ) : !distSelectedPcc ? (
                      <div style={{ padding: 12, fontSize: 12, color: '#94a3b8' }}>{t('pickPccFirst')}</div>
                    ) : usersForSelectedPcc.length === 0 ? (
                      <div style={{ padding: 12, fontSize: 12, color: '#94a3b8' }}>{t('noUsersPcc')}</div>
                    ) : usersForSelectedPcc.map((u) => {
                      const key = `${u.username}|${u.pcc}`
                      return (
                        <label key={key} style={{ display: 'grid', gridTemplateColumns: 'auto minmax(0, 1fr) auto', gap: 8, alignItems: 'center', padding: '8px 10px', borderBottom: '1px solid #f1f5f9', fontSize: 13, cursor: distApplyOnlyPcc || distBusy ? 'default' : 'pointer', opacity: distApplyOnlyPcc ? 0.72 : 1 }}>
                          <input
                            type="checkbox"
                            checked={distSelectedUsers.includes(key)}
                            disabled={distApplyOnlyPcc || distBusy}
                            onChange={() => toggleDistUser(u)}
                          />
                          <span style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={`${u.username} - ${u.pcc}`}>
                            {u.username} <span style={{ color: '#94a3b8' }}>· {u.pcc}</span>
                          </span>
                          <span style={{ color: '#94a3b8', fontSize: 12 }}>{u.preferredLanguage || ''}</span>
                        </label>
                      )
                    })}
                  </div>
                </div>
              </div>
            )}
            {distMsg ? <p style={{ fontSize: 12, color: '#334155', marginTop: 10 }}>{distMsg}</p> : null}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 14 }}>
              <button type="button" className="dashboard-row-btn" onClick={closeDistribute} disabled={distBusy}>{t('cancel')}</button>
              <button type="button" className="dashboard-row-btn primary" onClick={confirmDistribute} disabled={!canDistribute}>
                {distBusy ? t('distributing') : t('distBtn', { n: distributionTargetCount })}
              </button>
            </div>
          </div>
        </div>
      ) : null}
      {configScript ? (
        <DashboardScriptConfigModal
          open={Boolean(configScript)}
          script={configScript}
          session={dashboardRuntimeSession}
          env={getDashboardEnvironment()}
          token={token}
          onClose={() => setConfigScript(null)}
          onSaved={() => {
            setRepoMessage('Configuracion guardada para tu usuario.')
            setReloadTick((value) => value + 1)
          }}
        />
      ) : null}
      {repoPreview ? (
        <div
          role="dialog"
          aria-modal="true"
          onClick={closeRepoPreview}
          style={{ position: 'fixed', inset: 0, zIndex: 10000, background: 'rgba(15,23,42,.62)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 18 }}
        >
          <div onClick={(event) => event.stopPropagation()} style={{ width: 'min(1180px, 96vw)', maxHeight: '92vh', background: '#fff', borderRadius: 8, overflow: 'hidden', boxShadow: '0 24px 64px rgba(15,23,42,.35)' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, padding: '12px 16px', borderBottom: '1px solid #e5e7eb' }}>
              <div>
                <div style={{ fontSize: 11, textTransform: 'uppercase', color: '#64748b', fontWeight: 700 }}>{t('preview')}</div>
                <strong>{humanizeScriptName(repoPreview.name)}</strong>
              </div>
              <button type="button" className="dashboard-row-btn" onClick={closeRepoPreview}>{t('close')}</button>
            </div>
            <div style={{ height: '70vh', background: '#f8fafc' }}>
              <iframe
                title="Vista previa del script"
                src={repoPreview.url}
                sandbox="allow-scripts allow-forms allow-popups allow-modals allow-same-origin"
                style={{ width: '100%', height: '100%', border: 0, background: '#fff' }}
              />
            </div>
          </div>
        </div>
      ) : null}
    </main>
  )
}

function pageWindow(current, total) {
  const delta = 1
  const start = Math.max(1, current - delta)
  const end = Math.min(total, current + delta)
  const pages = []
  for (let i = start; i <= end; i += 1) pages.push(i)
  return pages
}

/* Visual determinístico por script: color + icono estables según uuid/nombre,
   para que cada script se distinga de los demás en el grid y el repo. */
const SCRIPT_ICONS = ['window', 'form', 'doc', 'bolt', 'tag', 'gear']

function scriptVisual(script, fallback) {
  const seed = String(script?.uuid || script?.name || script?.namescript || fallback || 'script')
  let hash = 0
  for (let i = 0; i < seed.length; i += 1) {
    hash = ((hash << 5) - hash + seed.charCodeAt(i)) | 0
  }
  const abs = Math.abs(hash)
  return {
    color: abs % 8,
    icon: SCRIPT_ICONS[Math.floor(abs / 8) % SCRIPT_ICONS.length],
  }
}

function DashboardIcon({ name }) {
  if (name === 'bell') {
    return (
      <svg viewBox="0 0 16 16" aria-hidden="true">
        <path fill="currentColor" d="M8 15a2.2 2.2 0 0 0 2.1-1.55H5.9A2.2 2.2 0 0 0 8 15Zm5.2-3.7-.9-.9V6.7A4.3 4.3 0 0 0 9 2.5V2a1 1 0 1 0-2 0v.5a4.3 4.3 0 0 0-3.3 4.2v3.7l-.9.9a.7.7 0 0 0 .5 1.2h9.4a.7.7 0 0 0 .5-1.2ZM5.1 10V6.7a2.9 2.9 0 0 1 5.8 0V10l.8.8H4.3l.8-.8Z" />
      </svg>
    )
  }
  if (name === 'check') {
    return (
      <svg viewBox="0 0 16 16" aria-hidden="true">
        <path fill="currentColor" d="M16 8A8 8 0 1 1 0 8a8 8 0 0 1 16 0ZM6.97 11.03l5-5a.75.75 0 0 0-1.06-1.06L6.44 9.44 4.53 7.53a.75.75 0 0 0-1.06 1.06l2.44 2.44a.75.75 0 0 0 1.06 0Z" />
      </svg>
    )
  }
  if (name === 'warning') {
    return (
      <svg viewBox="0 0 16 16" aria-hidden="true">
        <path fill="currentColor" d="M7.13 1.47a1 1 0 0 1 1.74 0l6.99 12.1A1 1 0 0 1 14.99 15H1.01a1 1 0 0 1-.87-1.5L7.13 1.47ZM8 5a.75.75 0 0 0-.75.75v3.5a.75.75 0 0 0 1.5 0v-3.5A.75.75 0 0 0 8 5Zm0 7a1 1 0 1 0 0-2 1 1 0 0 0 0 2Z" />
      </svg>
    )
  }
  if (name === 'grid') {
    return (
      <svg viewBox="0 0 16 16" aria-hidden="true">
        <path fill="currentColor" d="M1 2.5A1.5 1.5 0 0 1 2.5 1h4A1.5 1.5 0 0 1 8 2.5v4A1.5 1.5 0 0 1 6.5 8h-4A1.5 1.5 0 0 1 1 6.5v-4Zm7 7A1.5 1.5 0 0 1 9.5 8h4A1.5 1.5 0 0 1 15 9.5v4a1.5 1.5 0 0 1-1.5 1.5h-4A1.5 1.5 0 0 1 8 13.5v-4Z" />
      </svg>
    )
  }
  if (name === 'chevron') {
    return (
      <svg viewBox="0 0 16 16" aria-hidden="true">
        <path fill="currentColor" d="M6.22 3.22a.75.75 0 0 1 1.06 0l4.25 4.25a.75.75 0 0 1 0 1.06l-4.25 4.25a.75.75 0 0 1-1.06-1.06L9.94 8 6.22 4.28a.75.75 0 0 1 0-1.06Z" />
      </svg>
    )
  }
  if (name === 'chevron-left') {
    return (
      <svg viewBox="0 0 16 16" aria-hidden="true">
        <path fill="currentColor" d="M9.78 3.22a.75.75 0 0 1 0 1.06L6.06 8l3.72 3.72a.75.75 0 1 1-1.06 1.06L4.47 8.53a.75.75 0 0 1 0-1.06l4.25-4.25a.75.75 0 0 1 1.06 0Z" />
      </svg>
    )
  }
  if (name === 'search') {
    return (
      <svg viewBox="0 0 16 16" aria-hidden="true">
        <path fill="currentColor" d="M11.74 10.34a6 6 0 1 0-1.4 1.4l3.2 3.2a1 1 0 0 0 1.42-1.42l-3.22-3.18ZM3 7a4 4 0 1 1 8 0 4 4 0 0 1-8 0Z" />
      </svg>
    )
  }
  if (name === 'sparkle') {
    return (
      <svg viewBox="0 0 16 16" aria-hidden="true">
        <path fill="currentColor" d="M8 0l1.6 4.4L14 6l-4.4 1.6L8 12 6.4 7.6 2 6l4.4-1.6L8 0Zm5.2 8.2.7 2 2 .7-2 .7-.7 2-.7-2-2-.7 2-.7.7-2Z" />
      </svg>
    )
  }
  if (name === 'window') {
    return (
      <svg viewBox="0 0 16 16" aria-hidden="true">
        <path fill="currentColor" d="M2 3.5A1.5 1.5 0 0 1 3.5 2h9A1.5 1.5 0 0 1 14 3.5v9a1.5 1.5 0 0 1-1.5 1.5h-9A1.5 1.5 0 0 1 2 12.5v-9ZM3.5 3a.5.5 0 0 0-.5.5V5h10V3.5a.5.5 0 0 0-.5-.5h-9ZM13 6H3v6.5a.5.5 0 0 0 .5.5h9a.5.5 0 0 0 .5-.5V6Z" />
      </svg>
    )
  }
  if (name === 'form') {
    return (
      <svg viewBox="0 0 16 16" aria-hidden="true">
        <path fill="currentColor" d="M2.5 3a.5.5 0 0 0 0 1h11a.5.5 0 0 0 0-1h-11ZM2 6.5a.5.5 0 0 1 .5-.5h11a.5.5 0 0 1 0 1h-11a.5.5 0 0 1-.5-.5Zm0 3a.5.5 0 0 1 .5-.5h11a.5.5 0 0 1 0 1h-11a.5.5 0 0 1-.5-.5Zm0 3a.5.5 0 0 1 .5-.5h7a.5.5 0 0 1 0 1h-7a.5.5 0 0 1-.5-.5Z" />
      </svg>
    )
  }
  if (name === 'doc') {
    return (
      <svg viewBox="0 0 16 16" aria-hidden="true">
        <path fill="currentColor" d="M4 0a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h8a2 2 0 0 0 2-2V4.5L9.5 0H4Zm5.5 1.5v2a1 1 0 0 0 1 1h2L9.5 1.5ZM5 8a.5.5 0 0 1 .5-.5h5a.5.5 0 0 1 0 1h-5A.5.5 0 0 1 5 8Zm0 2.5a.5.5 0 0 1 .5-.5h5a.5.5 0 0 1 0 1h-5a.5.5 0 0 1-.5-.5Z" />
      </svg>
    )
  }
  if (name === 'bolt') {
    return (
      <svg viewBox="0 0 16 16" aria-hidden="true">
        <path fill="currentColor" d="M5.52.359A.5.5 0 0 1 6 0h4a.5.5 0 0 1 .474.658L8.694 6H12.5a.5.5 0 0 1 .395.807l-7 9a.5.5 0 0 1-.873-.454L6.823 9.5H3.5a.5.5 0 0 1-.48-.641l2.5-8.5Z" />
      </svg>
    )
  }
  if (name === 'tag') {
    return (
      <svg viewBox="0 0 16 16" aria-hidden="true">
        <path fill="currentColor" d="M2 1a1 1 0 0 0-1 1v4.586a1 1 0 0 0 .293.707l7 7a1 1 0 0 0 1.414 0l4.586-4.586a1 1 0 0 0 0-1.414l-7-7A1 1 0 0 0 6.586 1H2Zm4 3.5a1.5 1.5 0 1 1-3 0 1.5 1.5 0 0 1 3 0Z" />
      </svg>
    )
  }
  if (name === 'gear') {
    return (
      <svg viewBox="0 0 16 16" aria-hidden="true">
        <path fill="currentColor" d="M9.405 1.05c-.413-1.4-2.397-1.4-2.81 0l-.1.34a1.464 1.464 0 0 1-2.105.872l-.31-.17c-1.283-.698-2.686.705-1.987 1.987l.169.311c.446.82.023 1.841-.872 2.105l-.34.1c-1.4.413-1.4 2.397 0 2.81l.34.1a1.464 1.464 0 0 1 .872 2.105l-.17.31c-.698 1.283.705 2.686 1.987 1.987l.311-.169a1.464 1.464 0 0 1 2.105.872l.1.34c.413 1.4 2.397 1.4 2.81 0l.1-.34a1.464 1.464 0 0 1 2.105-.872l.31.17c1.283.698 2.686-.705 1.987-1.987l-.169-.311a1.464 1.464 0 0 1 .872-2.105l.34-.1c1.4-.413 1.4-2.397 0-2.81l-.34-.1a1.464 1.464 0 0 1-.872-2.105l.17-.31c.698-1.283-.705-2.686-1.987-1.987l-.311.169a1.464 1.464 0 0 1-2.105-.872l-.1-.34ZM8 10.93a2.929 2.929 0 1 1 0-5.86 2.929 2.929 0 0 1 0 5.858Z" />
      </svg>
    )
  }
  if (name === 'trash') {
    return (
      <svg viewBox="0 0 16 16" aria-hidden="true">
        <path fill="currentColor" d="M5.5 5.5A.5.5 0 0 1 6 6v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5Zm2.5 0a.5.5 0 0 1 .5.5v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5Zm3 .5a.5.5 0 0 0-1 0v6a.5.5 0 0 0 1 0V6Z" />
        <path fill="currentColor" d="M14.5 3a1 1 0 0 1-1 1H13v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V4h-.5a1 1 0 0 1-1-1V2a1 1 0 0 1 1-1H6a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1h3.5a1 1 0 0 1 1 1v1ZM4.118 4 4 4.059V13a1 1 0 0 0 1 1h6a1 1 0 0 0 1-1V4.059L11.882 4H4.118Z" />
      </svg>
    )
  }
  return (
    <svg viewBox="0 0 16 16" aria-hidden="true">
      <path fill="currentColor" d="M8 1a7 7 0 1 0 7 7h-1.5A5.5 5.5 0 1 1 8 2.5V5l3-3-3-3v2Z" />
    </svg>
  )
}

export default DashboardPage
